import { IonButton, IonIcon, IonModal, IonSpinner } from '@ionic/react'
import { msg } from '@lingui/core/macro'
import { useLingui } from '@lingui/react'
import {
  alertCircleOutline,
  cloudUploadOutline,
  imagesOutline,
} from 'ionicons/icons'
import { useState } from 'react'
import type { OrderPhotoView } from '../../hooks/useOrderPhotos'
import { OrderPhotoViewerModal } from '../../modals/OrderPhotoViewerModal'

type OrderPhotosSectionProps = {
  photos: OrderPhotoView[]
  fetching: boolean
  /** Present when the fetch failed — it carries the only way out (retry) */
  error?: { onRetry: () => void }
  /** Deletes a failed queued photo from the device (viewer trash action);
   * resolves false when the provider refused (still queued). */
  onDiscard?: (clientId: string) => Promise<boolean>
}

const thumbLabel = (
  photo: { pending: boolean; failed: boolean },
  showable: boolean,
  _: ReturnType<typeof useLingui>['_'],
): string => {
  if (!showable) return _(msg`Photo not available offline`)
  if (photo.failed) return _(msg`Photo, upload failed, will retry`)
  if (photo.pending) return _(msg`Photo, pending upload`)
  return _(msg`View photo`)
}

const thumbShowable = (
  photo: { thumbSrc: string },
  failedSrcs: ReadonlySet<string>,
): boolean => Boolean(photo.thumbSrc) && !failedSrcs.has(photo.thumbSrc)

// Presentational gallery — the photos page owns data fetching and capture.
export const OrderPhotosSection = ({
  photos,
  fetching,
  error,
  onDiscard,
}: OrderPhotosSectionProps) => {
  const { _ } = useLingui()

  // Keyed by identity: the list reorders under the open modal when a drain
  // lands (queued → server segment), so an index would jump photos.
  const [viewerClientId, setViewerClientId] = useState<string | null>(null)

  // Presigned URLs that failed to load (offline / expired) — render the
  // placeholder instead of the WebView's broken-image glyph. Keyed by src,
  // so freshly minted URLs after a refetch retry naturally.
  const [failedSrcs, setFailedSrcs] = useState<ReadonlySet<string>>(new Set())
  const markSrcFailed = (src: string) =>
    setFailedSrcs((current) => {
      if (current.has(src)) return current
      const next = new Set(current)
      next.add(src)
      return next
    })
  const openViewerIndex =
    viewerClientId === null
      ? null
      : photos.findIndex((photo) => photo.clientId === viewerClientId)
  const viewerIndex =
    openViewerIndex === null || openViewerIndex < 0 ? null : openViewerIndex

  // Distinct empty-grid states: loading, failed (never "no photos" on error)
  const showLoading = photos.length === 0 && fetching
  const showError = photos.length === 0 && !fetching && error !== undefined
  const showEmpty = photos.length === 0 && !fetching && error === undefined

  return (
    <div className='order-photos'>
      <div className='order-photos__header'>
        <div className='order-photos__title'>
          {_(msg`Photos`)}
          {photos.length > 0 && (
            <span className='order-photos__count'>{photos.length}</span>
          )}
        </div>
      </div>

      {showLoading && (
        <div className='order-photos__empty'>
          <IonSpinner name='crescent' />
        </div>
      )}

      {showError && (
        <div className='order-photos__empty'>
          <IonIcon icon={alertCircleOutline} />
          {_(msg`Photos could not be loaded. Check your connection.`)}
          <IonButton size='small' fill='outline' onClick={error?.onRetry}>
            {_(msg`Retry`)}
          </IonButton>
        </div>
      )}

      {showEmpty && (
        <div className='order-photos__empty'>
          <IonIcon icon={imagesOutline} />
          {_(msg`No photos yet. Document the job site, dockbox or tickets.`)}
        </div>
      )}

      {photos.length > 0 && (
        <div className='order-photos__grid'>
          {photos.map((photo) => (
            <button
              key={photo.clientId}
              type='button'
              className='order-photos__thumb'
              onClick={() => setViewerClientId(photo.clientId)}
              aria-label={thumbLabel(
                photo,
                thumbShowable(photo, failedSrcs),
                _,
              )}
              // No preview (not loaded yet, lost, or unreachable offline) —
              // nothing to open full-screen. A failed queued photo stays
              // tappable: the viewer is where it can be discarded.
              disabled={!thumbShowable(photo, failedSrcs) && !photo.failed}
            >
              {thumbShowable(photo, failedSrcs) ? (
                <img
                  src={photo.thumbSrc}
                  alt=''
                  loading='lazy'
                  onError={() => markSrcFailed(photo.thumbSrc)}
                />
              ) : (
                <span className='order-photos__thumb-placeholder'>
                  <IonIcon icon={imagesOutline} />
                </span>
              )}
              {photo.pending && (
                // State is part of the button's own label; the badge is visual
                <span
                  className={`order-photos__badge ${
                    photo.failed ? 'order-photos__badge--failed' : ''
                  }`}
                  aria-hidden='true'
                >
                  <IonIcon
                    icon={
                      photo.failed ? alertCircleOutline : cloudUploadOutline
                    }
                  />
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      <IonModal
        isOpen={viewerIndex !== null}
        onDidDismiss={() => setViewerClientId(null)}
      >
        {viewerIndex !== null && (
          <OrderPhotoViewerModal
            photos={photos.map((photo) => ({
              clientId: photo.clientId,
              url: photo.fullSrc,
              uploadedByName: photo.uploadedByName,
              takenAt: photo.takenAt,
              createdAt: photo.createdAt,
              discardable: photo.failed,
            }))}
            startIndex={viewerIndex}
            onDismiss={() => setViewerClientId(null)}
            onDiscard={
              onDiscard
                ? async (clientId) => {
                    const discarded = await onDiscard(clientId)
                    if (discarded) setViewerClientId(null)
                  }
                : undefined
            }
          />
        )}
      </IonModal>
    </div>
  )
}
